import { DataContext } from '@philips-emr/tws-core/lib/component/context';
import SubmittableFragmentContext from '@philips-emr/tws-core/lib/component/context/submittable-fragment-context';

import Engine from '..';
import { FragmentMetadata, Node } from '../metadata';

import ComponentNode from './component-node';
import provideContainer, { Container } from './dependency/container-provider';

interface FragmentArgs {
  /** The engine instance. */
  engine: Engine;
  /** The instance uuid (<Fragment fragment="some-fragment" uuid="some-uuid" instance-uuid="?generated-in-runtime">). */
  instanceUUID: string;
  /** The fragment metadata name. */
  name: string;
  /** The parent instance. */
  parent: FragmentNode | null;
  /** If the fragment is submittable. */
  submittable?: boolean;
}

/**
 * Fragment node representation.
 * It's responsible to handle all stuff related to fragment children state and event.
 */
class FragmentNode {

  private children = new Map<string, ComponentNode>();

  private container: Container;
  private dataContext: DataContext;
  private submittableFragmentContext: SubmittableFragmentContext | null;

  private engine: Engine;

  private instanceUUID: string;
  private name: string;

  private parent: FragmentNode | null = null;

  constructor(args: FragmentArgs) {
    this.engine = args.engine;
    this.instanceUUID = args.instanceUUID;
    this.name = args.name;
    this.parent = args.parent;

    this.submittableFragmentContext = args.submittable ? new SubmittableFragmentContext() : null;
    this.dataContext = new DataContext(args.parent ? args.parent.dataContext : null);
    this.container = provideContainer({
      dataContext: this.dataContext,
      fragment: this,
      submittableFragmentContext: this.submittableFragmentContext,
    });
  }

  private getKey(uuid: string, instanceUUID: string): string {
    return `${uuid}_${instanceUUID}`;
  }

  /**
   * Discard all stuff related to the component instance.
   *
   * @param uuid the component uuid that must be discarded.
   * @param instanceUUID the component instance uuid.
   */
  discard(uuid: string, instanceUUID: string): void {
    const key = this.getKey(uuid, instanceUUID);

    const child = this.children.get(key);
    if (child) {
      child.getAPI().discard();
    }
    this.children.delete(key);
  }

  /**
   * Destroy all the resources created by this instance of fragment.
   */
  destroy(): void {
    this.dataContext.destroy();
  }

  /**
   * Retrieves the ComponentNode by the uuid, it only returns a instance when the component is rendered.
   * A Component that is not rendered, does not exists.
   * If the component is @repeated, it's not possible to retrieve any component instance.
   *
   * @param uuid the child uuid.
   * @returns the ComponentNode instance, or null.
   * @throws error if the component is @repeated.
   */
  getChild(uuid: string): ComponentNode | null {
    const nodes = Array.from(this.children.values())
      .filter(a => a.getUUID() === uuid);

    if (nodes.length > 1) {
      throw new Error('[Engine]: It is not possible to get @repeated components.');
    }

    return nodes[0] || null;
  }

  /**
   * Retrieves the child metadata, by uuid.
   *
   * @param uuid the child uuid.
   * @returns the metadata Node instance.
   */
  getChildMetadata(uuid: string): Node | null {
    const fragment = this.getMetadata();
    return fragment.getNode(uuid);
  }

  /**
   * Retrieves the fragment Dependency Injection container.
   *
   * @returns the container instance.
   */
  getContainer(): Container {
    return this.container;
  }

  /**
   * Retrieves the fragment DataContext instance.
   * @returns the DataContext instance.
   */
  getDataContext(): DataContext {
    return this.dataContext;
  }

  /**
   * Retrieves the fragment SubmittableFragmentContext instance.
   * @returns the SubmittableFragmentContext instance.
   */
  getSubmittableFragmentContext(): SubmittableFragmentContext | null {
    return this.submittableFragmentContext;
  }

  /**
   * Retrieve the Engine instance.
   * @returns the Engine instance.
   */
  getEngine(): Engine {
    return this.engine;
  }

  /**
   * Retrieve the hashCode of the fragment instance.
   * It's compose by the parent's hashCode + it's uuid, recursivelly.
   *
   * @returns the hashCode.
   */
  getHashCode(): string {
    // TODO: create hash using the node uuid
    const hashCode = this.instanceUUID;

    if (this.parent) {
      const parentHashCode = this.parent.getHashCode();
      return `${parentHashCode}_${hashCode}`;
    }

    return hashCode;
  }

  /**
   * Retrieves the frament name.
   *
   * @returns the fragment name.
   */
  getName(): string {
    return this.name;
  }

  /**
   * Retrieves the FragmentMetadata instance.
   *
   * @returns the FragmentMetadata instance.
   */
  getMetadata(): FragmentMetadata {
    const metadata = this.engine.getFragmentMetadata(this.name);

    if (!metadata) {
      throw new Error(`[FragmentNode]: there is no metadata named '${this.name}.'`);
    }

    return metadata;
  }

  /**
   * Get or create a ComponentNode for the uuid specified.
   *
   * @param uuid the component metadata uuid.
   * @param instanceUUID the component instance uuid.
   */
  provideNode(uuid: string, instanceUUID: string): ComponentNode {
    const key = this.getKey(uuid, instanceUUID);

    let child = this.children.get(key);

    if (!child) {
      const nodeMetadata = this.getChildMetadata(uuid);

      if (!nodeMetadata) throw new Error(`[FragmentNode]: there is not component with uuid '${uuid}, ${instanceUUID}.'`);

      child = new ComponentNode({
        instanceUUID,
        nodeMetadata,
        owner: this,
        uuid,
      });
      this.children.set(key, child);
    }
    return child;
  }

}

export default FragmentNode;
